import { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import ButtonIcon from '../ui/ButtonIcon';
import Popover from '../ui/Popover';

export interface SectionSortOption<T extends string> {
  value: T;
  label: React.ReactNode;
}

interface SectionSortMenuProps<T extends string> {
  sortMode: T;
  sortOptions: SectionSortOption<T>[];
  showSortOptions: boolean;
  onSortChange: (mode: T) => void;
  onAdd: () => void;
  addLabel: React.ReactNode;
  addTitle: string;
  optionsTitle: string;
}

function SectionSortMenu<T extends string>({
  sortMode,
  sortOptions,
  showSortOptions,
  onSortChange,
  onAdd,
  addLabel,
  addTitle,
  optionsTitle,
}: SectionSortMenuProps<T>) {
  const [isOpen, setIsOpen] = useState(false);

  const handleAdd = () => {
    onAdd();
    setIsOpen(false);
  };

  const handleSortChange = (mode: T) => {
    onSortChange(mode);
    setIsOpen(false);
  };

  if (!showSortOptions) {
    return (
      <ButtonIcon
        icon="add"
        title={addTitle}
        onClick={handleAdd}
        className="side-nav-section-add"
      />
    );
  }

  return (
    <Popover
      open={isOpen}
      onOpenChange={setIsOpen}
      content={
        <div className="min-w-48">
          <button type="button" className="menu-item" onClick={handleAdd}>
            <span>{addLabel}</span>
          </button>
          <div className="menu-divider" />
          <div className="menu-header">
            <Trans>Sort</Trans>
          </div>
          <div className="flex flex-col">
            {sortOptions.map((option) => {
              const isSelected = sortMode === option.value;

              return (
                <button
                  key={option.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={isSelected}
                  onClick={() => handleSortChange(option.value)}
                  className="group menu-item justify-between"
                >
                  <span>{option.label}</span>
                  {isSelected && (
                    <svg
                      className="text-copy-secondary group-hover:text-copy-primary h-2 w-2.5 fill-none stroke-current stroke-2"
                      viewBox="0 0 9 8"
                    >
                      <path d="M1 4.31034L3.69231 7L8 1" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      }
      placement="bottom-start"
      role="menu"
    >
      <ButtonIcon icon="more" title={optionsTitle} iconClassName="rotate-90" />
    </Popover>
  );
}

export default SectionSortMenu;
